/**
 * El Embedded Signup de Meta para un canal de la Cloud API (D29): precarga el SDK, abre el popup en el
 * click y conecta el canal con lo que Meta devuelve.
 *
 * Qué significa cada evento, y cuándo alcanza para conectar, está en `embedded-signup.ts`, en puro; acá
 * sólo está lo del navegador: el SDK, el listener de `postMessage` y los timers.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Channel, conversationsApi } from "@/services/conversations.api";
import { FacebookSdk, cargarSdkDeFacebook } from "@/services/facebook-sdk";
import {
  ESPERA_MS,
  Entrada,
  FLUJO_NUEVO,
  Flujo,
  TIMEOUT_MS,
  avanzar,
  esDeFacebook,
  mensajeDelAlta,
} from "./embedded-signup";

export type FaseDelAlta =
  /** Se está cargando el SDK o la config de la app. */
  | "cargando"
  /** Este ambiente no puede abrir el flujo: falta la app de Meta o el SDK no cargó. */
  | "no_disponible"
  | "lista"
  /** El popup de Meta está abierto. */
  | "popup"
  /** Se está canjeando el código y conectando el número. */
  | "conectando"
  | "error";

export interface EstadoDelAlta {
  fase: FaseDelAlta;
  /** El motivo de `no_disponible` o de `error`, o el aviso de una cancelación. */
  motivo?: string;
}

export function useEmbeddedSignup(channelId: string | undefined, onConectado: (channel: Channel) => void) {
  const [estado, setEstado] = useState<EstadoDelAlta>({ fase: "cargando" });
  const sdk = useRef<FacebookSdk | null>(null);
  const configId = useRef<string | null>(null);
  const flujo = useRef<Flujo>(FLUJO_NUEVO);
  const timers = useRef<number[]>([]);
  const escucha = useRef<((e: MessageEvent) => void) | null>(null);
  // Los callbacks de Meta llegan fuera de React: leen lo último por ref, no lo de su render.
  const canal = useRef(channelId);
  canal.current = channelId;
  const alConectar = useRef(onConectado);
  alConectar.current = onConectado;
  const montado = useRef(true);

  const limpiar = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    if (escucha.current) window.removeEventListener("message", escucha.current);
    escucha.current = null;
  }, []);

  const fijar = (e: EstadoDelAlta) => {
    if (montado.current) setEstado(e);
  };

  // Se precarga al montar: en el click ya tiene que estar, o `FB.login` deja de ser sincrónico.
  useEffect(() => {
    montado.current = true;
    conversationsApi
      .cloudConfig()
      .then((c) => {
        if (!c.appId || !c.configId) {
          throw new Error("Falta configurar la app de Meta en este ambiente.");
        }
        configId.current = c.configId;
        return cargarSdkDeFacebook(c.appId, c.graphVersion);
      })
      .then((FB) => {
        sdk.current = FB;
        fijar({ fase: "lista" });
      })
      .catch((e) => fijar({ fase: "no_disponible", motivo: mensajeDelAlta(e) }));
    return () => {
      montado.current = false;
      limpiar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicar = (entrada: Entrada) => {
    const { flujo: siguiente, paso } = avanzar(flujo.current, entrada);
    flujo.current = siguiente;
    switch (paso.accion) {
      case "nada":
        return;
      case "esperar":
        timers.current.push(window.setTimeout(() => aplicar({ tipo: "espera_vencida" }), ESPERA_MS));
        return;
      case "conectar": {
        limpiar();
        const id = canal.current;
        if (!id) return;
        fijar({ fase: "conectando" });
        conversationsApi
          .embeddedSignup(id, paso.pedido)
          .then((ch) => {
            fijar({ fase: "lista" });
            alConectar.current(ch);
          })
          .catch((e) => fijar({ fase: "error", motivo: mensajeDelAlta(e) }));
        return;
      }
      case "terminar":
        limpiar();
        if (paso.resultado.kind === "cancelado") {
          fijar({ fase: "lista", motivo: paso.resultado.mensaje ?? "Se canceló la conexión con Facebook." });
        } else {
          fijar({ fase: "error", motivo: paso.resultado.mensaje });
        }
    }
  };

  /**
   * Para el `onClick`. `FB.login` corre acá, sincrónico: cualquier `await` antes haría que el navegador
   * bloquee el popup.
   */
  const conectar = () => {
    const FB = sdk.current;
    if (!FB || !configId.current || !canal.current) return;
    limpiar();
    flujo.current = FLUJO_NUEVO;
    const onMessage = (e: MessageEvent) => {
      if (esDeFacebook(e.origin)) aplicar({ tipo: "mensaje", data: e.data });
    };
    escucha.current = onMessage;
    window.addEventListener("message", onMessage);
    timers.current.push(window.setTimeout(() => aplicar({ tipo: "timeout" }), TIMEOUT_MS));
    fijar({ fase: "popup" });
    // El SDK rechaza un callback `async`: tiene que ser una función común.
    FB.login((response) => aplicar({ tipo: "login", code: response?.authResponse?.code ?? null }), {
      config_id: configId.current,
      response_type: "code",
      override_default_response_type: true,
      extras: { setup: {}, featureType: "whatsapp_business_app_onboarding", sessionInfoVersion: "3" },
    });
  };

  return { estado, conectar };
}
